import { z } from 'zod';

import { zFirestampOptional } from '@elara/firebolt/zod-types';

import { WidgetImageSchema } from './image-schemas.js';
import { ItineraryItemEventTypeSchema } from './itinerary-item-event-schemas.js';
import { AddressSchema, CoordinatesSchema } from './shared-schemas.js';

/** Categories a place can be classified into (derived from Google Place types). */
export const PlaceCategorySchema = z.enum([
  'dining',
  'culture',
  'entertainment',
  'nature',
  'shopping',
  'wellness',
  'nightlife',
]);

/**
 * Place document identity: provider IDs live here, not as Firestore doc id.
 * Firestore document id is generated; referenceId in widgets/shortlist/itinerary is that doc id.
 */
export const PlaceIdsSchema = z.object({
  googlePlaceId: z.string().nullish(),
  hotelId: z.string().nullish().describe('Firestore hotel document id when this place is a hotel in our database'),
});

export type PlaceIds = z.infer<typeof PlaceIdsSchema>;

/** Single opening point within a week (day-of-week plus time). */
export const PlaceOpeningPointSchema = z.object({
  day: z.number().int().min(0).max(6).nullish(), // 0 = Sunday, 6 = Saturday (matches Google)
  hour: z.number().int().min(0).max(23).nullish(),
  minute: z.number().int().min(0).max(59).nullish(),
});

/** Single opening period (open/close) as structured day/hour/minute. */
export const PlaceOpeningPeriodSchema = z.object({
  open: PlaceOpeningPointSchema.nullish(),
  close: PlaceOpeningPointSchema.nullish(),
});

export type PlaceOpeningPeriod = z.infer<typeof PlaceOpeningPeriodSchema>;

/** Structured opening hours with weekday text and periods from Google. */
export const PlaceOpeningHoursSchema = z.object({
  weekdayText: z.array(z.string()).nullish(),
  periods: z.array(PlaceOpeningPeriodSchema).nullish(),
});

export type PlaceOpeningHours = z.infer<typeof PlaceOpeningHoursSchema>;

/**
 * Per-person spend band Google publishes for a place (Places API `priceRange`).
 *
 * Distinct from `priceLevel`, which is a coarse 0-4 enum: this carries real currency
 * amounts (e.g. "$100+" for a fine-dining room), which is the signal that actually
 * helps a client judge a restaurant. Google frequently returns only `startPrice`.
 */
export const PlacePriceRangeSchema = z.object({
  startPrice: z.number().nonnegative().nullish(),
  endPrice: z.number().nonnegative().nullish(),
  currencyCode: z.string().nullish().describe('ISO 4217 code, e.g. USD'),
});

export type PlacePriceRange = z.infer<typeof PlacePriceRangeSchema>;

/** Full set of Google Maps links for a place (see GoogleMapsLinks in Places API). */
export const GoogleMapsLinksSchema = z.object({
  directionsUri: z.string().url().nullish(),
  placeUri: z.string().url().nullish(),
  writeAReviewUri: z.string().url().nullish(),
  reviewsUri: z.string().url().nullish(),
  photosUri: z.string().url().nullish(),
});

export type GoogleMapsLinks = z.infer<typeof GoogleMapsLinksSchema>;

/** Google Places accessibility options for a place. */
export const AccessibilityOptionsSchema = z.object({
  wheelchairAccessibleParking: z.boolean().nullish(),
  wheelchairAccessibleEntrance: z.boolean().nullish(),
  wheelchairAccessibleRestroom: z.boolean().nullish(),
  wheelchairAccessibleSeating: z.boolean().nullish(),
});

export type AccessibilityOptions = z.infer<typeof AccessibilityOptionsSchema>;

/** Google Places parking options for a place. */
export const ParkingOptionsSchema = z.object({
  freeParkingLot: z.boolean().nullish(),
  paidParkingLot: z.boolean().nullish(),
  freeStreetParking: z.boolean().nullish(),
  paidStreetParking: z.boolean().nullish(),
  valetParking: z.boolean().nullish(),
  freeGarageParking: z.boolean().nullish(),
  paidGarageParking: z.boolean().nullish(),
});

export type ParkingOptions = z.infer<typeof ParkingOptionsSchema>;

/** Google Places EV charge options for a place. */
export const EvChargeConnectorAggregationSchema = z.object({
  type: z.string().nullish(),
  maxChargeRateKw: z.number().nullish(),
  count: z.number().int().nullish(),
  availabilityLastUpdateTime: z.string().nullish(),
  availableCount: z.number().int().nullish(),
  outOfServiceCount: z.number().int().nullish(),
});

export const EvChargeOptionsSchema = z.object({
  connectorCount: z.number().int().nullish(),
  connectorAggregation: z.array(EvChargeConnectorAggregationSchema).nullish(),
});

export type EvChargeOptions = z.infer<typeof EvChargeOptionsSchema>;

/**
 * Place data schema. Normalized subset of Google Places (and future sources).
 * All stored fields use camelCase (ids.googlePlaceId, ids.hotelId, fetchedAt, etc.).
 * Document id in Firestore is generated; never use Google Place ID as doc id.
 */
export const PlaceSchema = z.object({
  name: z.string().min(1),
  primaryType: z.string().nullish().describe('Google Places primaryType — the single most representative type for this place'),
  types: z.array(z.string()).default([]),

  // Denormalized classification resolved once at write time (upsertPlace) from
  // primaryType/types via the shared ruleset in `@elara/domain/models/place-category`.
  // Place types are immutable after fetch, so this never drifts and read paths
  // (itinerary expansion, booking grouping) avoid re-deriving it client-side.
  category: PlaceCategorySchema.nullish().describe('Resolved place category (denormalized from Google types)'),
  eventType: ItineraryItemEventTypeSchema.nullish().describe('Resolved itinerary event type (denormalized; e.g. place_dining)'),
  sources: z.array(z.string()).default([]).describe("e.g. ['google-places']"),
  fetchedAt: zFirestampOptional().nullish().describe('When place data was last fetched from provider'),
  ids: PlaceIdsSchema.nullish(),

  coordinates: CoordinatesSchema.nullish(),
  formattedAddress: z.string().nullish(),
  address: AddressSchema.nullish(),
  timeZone: z.string().nullish(),

  openingHours: PlaceOpeningHoursSchema.nullish(),
  phoneNumber: z.string().nullish(),
  internationalPhoneNumber: z.string().nullish(),
  website: z.string().url().nullish(),
  rating: z.number().min(0).max(5).nullish(),
  userRatingsTotal: z.number().int().nonnegative().nullish(),
  priceLevel: z.number().int().min(0).max(4).nullish(),
  priceRange: PlacePriceRangeSchema.nullish().describe('Google per-person spend band; richer than priceLevel'),
  googleBusinessStatus: z.string().nullish(),

  photos: z.array(WidgetImageSchema).default([]),
  googleMapsLinks: GoogleMapsLinksSchema.nullish(),

  editorialSummary: z.string().nullish().describe('Google editorial summary (LocalizedText.text)'),
  generativeSummary: z.string().nullish().describe('Google AI place summary (generativeSummary.overview.text)'),
  generativeSummaryDisclosureText: z
    .string()
    .nullish()
    .describe('Disclosure text for generative summary (generativeSummary.disclosureText.text)'),
  reviewSummary: z.string().nullish().describe('Google AI review summary (reviewSummary.text.text)'),
  neighborhoodSummary: z.string().nullish().describe('Google AI neighborhood summary'),

  // Google place attributes (booleans describing services/amenities)
  takeout: z.boolean().nullish(),
  delivery: z.boolean().nullish(),
  dineIn: z.boolean().nullish(),
  curbsidePickup: z.boolean().nullish(),
  reservable: z.boolean().nullish(),
  servesBreakfast: z.boolean().nullish(),
  servesLunch: z.boolean().nullish(),
  servesDinner: z.boolean().nullish(),
  servesBeer: z.boolean().nullish(),
  servesWine: z.boolean().nullish(),
  servesBrunch: z.boolean().nullish(),
  servesVegetarianFood: z.boolean().nullish(),
  outdoorSeating: z.boolean().nullish(),
  liveMusic: z.boolean().nullish(),
  menuForChildren: z.boolean().nullish(),
  servesCocktails: z.boolean().nullish(),
  servesDessert: z.boolean().nullish(),
  servesCoffee: z.boolean().nullish(),
  goodForChildren: z.boolean().nullish(),
  allowsDogs: z.boolean().nullish(),
  restroom: z.boolean().nullish(),
  goodForGroups: z.boolean().nullish(),
  goodForWatchingSports: z.boolean().nullish(),

  // Accessibility options (subset of Google AccessibilityOptions)
  accessibilityOptions: AccessibilityOptionsSchema.nullish(),

  // Parking options (subset of Google ParkingOptions)
  parkingOptions: ParkingOptionsSchema.nullish(),

  // EV charge options (subset of Google EVChargeOptions)
  evChargeOptions: EvChargeOptionsSchema.nullish(),

  // Hierarchical place relationships (Firestore place document IDs)
  subDestinationIds: z.array(z.string()).nullish().describe('Firestore place doc IDs of sub-destinations (e.g. attractions within a park)'),
  containingPlaceIds: z.array(z.string()).nullish().describe('Firestore place doc IDs of containing places (e.g. the city this place is in)'),

  // AI-generated content (populated lazily when place guide is opened)
  generated: z.object({
    description: z.string().describe('AI-generated description of this place'),
    advisory: z.string().nullish().describe('Optional advisory tip for visitors'),
    generatedAt: zFirestampOptional().describe('When AI content was generated'),
  }).nullish(),
});

export type PlaceData = z.infer<typeof PlaceSchema>;
